import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';

interface ImageUploaderProps {
  label: string;
  preview: string | null;
  onImageChange: (file: File | null) => void;
  onRemove: () => void;
  recommendedResolution?: string;
  maxSizeMB?: number;
}

export const ImageUploader = ({ label, preview, onImageChange, onRemove, recommendedResolution, maxSizeMB = 40 }: ImageUploaderProps) => {
  const MAX_IMAGE_SIZE = maxSizeMB * 1024 * 1024;
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast({
          title: "Archivo demasiado grande",
          description: "El tamaño máximo permitido es 40MB",
          variant: "destructive",
        });
        return;
      }
      onImageChange(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast({
          title: "Archivo demasiado grande",
          description: "El tamaño máximo permitido es 40MB",
          variant: "destructive",
        });
        return;
      }
      onImageChange(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold text-foreground">{label}</Label>
      
      {preview ? (
        <div className="relative group rounded-lg overflow-hidden border-2 border-border bg-muted h-64">
          <img
            src={preview}
            alt={label}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleClick}
              className="bg-white/90 hover:bg-white text-foreground"
            >
              <Upload className="h-4 w-4 mr-2" />
              Cambiar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onRemove}
              className="bg-destructive/90 hover:bg-destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative h-64 rounded-lg border-2 border-dashed transition-all cursor-pointer
            flex flex-col items-center justify-center gap-3 p-6
            ${isDragging 
              ? 'border-primary bg-primary/5 scale-105' 
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
          `}
        >
          <div className={`
            w-16 h-16 rounded-full flex items-center justify-center transition-colors
            ${isDragging ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
          `}>
            <ImageIcon className="h-8 w-8" />
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground mb-1">
              {isDragging ? 'Suelta la imagen aquí' : 'Haz clic o arrastra una imagen'}
            </p>
            <p className="text-sm text-muted-foreground">
              PNG, JPG, WEBP hasta {maxSizeMB}MB
            </p>
            {recommendedResolution && (
              <p className="text-xs text-muted-foreground mt-1">
                Resolución recomendada: {recommendedResolution}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Seleccionar archivo
          </Button>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};
